/**
 * WorkspaceSidebarContent - Shared sidebar content for desktop and mobile.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wand2, Settings2, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigationPreferences } from '@/hooks/useNavigationPreferences';
import { SortableToolItem } from './SortableToolItem';
import { MoreToggle } from './MoreToggle';

const MORE_EXPANDED_STORAGE_KEY = 'sidebar-more-expanded';

interface ToolsSidebarContentProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onManageTools: () => void;
}

export function ToolsSidebarContent({
  collapsed,
  onToggleCollapse,
  onManageTools,
}: ToolsSidebarContentProps) {
  const { t } = useTranslation();
  const { organizedTools, toggleFavorite, isLoading } = useNavigationPreferences();

  const [moreExpanded, setMoreExpanded] = useState(
    () => localStorage.getItem(MORE_EXPANDED_STORAGE_KEY) === 'true'
  );

  const handleToggleMore = useCallback(() => {
    setMoreExpanded((prev) => {
      const next = !prev;
      localStorage.setItem(MORE_EXPANDED_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const handleToggleFavorite = useCallback(
    (toolId: string, isFavorite: boolean) => toggleFavorite(toolId, !isFavorite),
    [toggleFavorite]
  );

  if (collapsed) {
    return (
      <CollapsedView
        tools={[...organizedTools.primary, ...organizedTools.collapsed]}
        isLoading={isLoading}
        onManageTools={onManageTools}
        onToggleCollapse={onToggleCollapse}
      />
    );
  }

  const hasMore = organizedTools.collapsed.length > 0;

  return (
    <>
      {/* Header (no settings cog any more) */}
      <div className="flex items-center justify-start px-3 py-4 border-b border-neutral-100 dark:border-neutral-800">
        <div className="p-2 rounded-xl bg-primary-100 dark:bg-primary-900/30">
          <Wand2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <span className="ml-3 text-lg font-bold text-neutral-900 dark:text-neutral-100">
          Tools
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1" aria-label="Tools Navigation">
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <div className="workspace-sidebar-primary">
              {organizedTools.primary.map((tool) => (
                <SortableToolItem
                  key={tool.id}
                  tool={tool}
                  isDraggable={false}
                  onToggleFavorite={() => handleToggleFavorite(tool.id, false)}
                  isFavorite
                />
              ))}
            </div>

            {hasMore && (
              <>
                <MoreToggle
                  expanded={moreExpanded}
                  onToggle={handleToggleMore}
                  count={organizedTools.collapsed.length}
                />
                {moreExpanded && (
                  <div className="workspace-sidebar-collapsed">
                    {organizedTools.collapsed.map((tool) => (
                      <SortableToolItem
                        key={tool.id}
                        tool={tool}
                        isDraggable={false}
                        onToggleFavorite={() => handleToggleFavorite(tool.id, false)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {moreExpanded && (
              <>
                <div className="my-2 border-t border-neutral-100 dark:border-neutral-800" />
                <button
                  type="button"
                  onClick={onManageTools}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg',
                    'text-sm font-semibold text-neutral-500 dark:text-neutral-400',
                    'hover:text-neutral-900 dark:hover:text-neutral-100',
                    'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                    'transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500'
                  )}
                  title={t('workspace.sidebar.manageTools')}
                  aria-label={t('workspace.sidebar.manageTools')}
                >
                  <Settings2 className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-left">{t('workspace.sidebar.manageTools')}</span>
                </button>
              </>
            )}
          </>
        )}
      </nav>

      {/* Footer: Collapse-Toggle */}
      <div className="border-t border-neutral-100 dark:border-neutral-800 p-2">
        <button
          onClick={onToggleCollapse}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 rounded-lg justify-start',
            'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
            'hover:bg-neutral-100 dark:hover:bg-neutral-800',
            'transition-colors text-sm'
          )}
          title={t('workspace.sidebar.collapse')}
          aria-label={t('workspace.sidebar.closeSidebar')}
        >
          <ChevronLeft className="h-4 w-4 flex-shrink-0" />
          <span>{t('workspace.sidebar.collapse')}</span>
        </button>
      </div>
    </>
  );
}

interface CollapsedViewProps {
  tools: ReturnType<typeof useNavigationPreferences>['organizedTools']['primary'];
  isLoading: boolean;
  onManageTools: () => void;
  onToggleCollapse: () => void;
}

function CollapsedView({ tools, isLoading, onManageTools, onToggleCollapse }: CollapsedViewProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center justify-center px-3 py-4 border-b border-neutral-100 dark:border-neutral-800">
        <div className="p-2 rounded-xl bg-primary-100 dark:bg-primary-900/30">
          <Wand2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-2 space-y-1" aria-label="Tools Navigation">
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          tools.map((tool) => (
            <SortableToolItem key={tool.id} tool={tool} isDraggable={false} collapsed />
          ))
        )}
        <button
          type="button"
          onClick={onManageTools}
          className={cn(
            'w-full flex items-center justify-center p-2.5 rounded-lg',
            'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100',
            'hover:bg-neutral-100 dark:hover:bg-neutral-800',
            'transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-primary-500'
          )}
          title={t('workspace.sidebar.manageTools')}
          aria-label={t('workspace.sidebar.manageTools')}
        >
          <Settings2 className="h-5 w-5" aria-hidden="true" />
        </button>
      </nav>

      <div className="border-t border-neutral-100 dark:border-neutral-800 p-2">
        <button
          onClick={onToggleCollapse}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 rounded-lg justify-center',
            'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
            'hover:bg-neutral-100 dark:hover:bg-neutral-800',
            'transition-colors text-sm'
          )}
          title={t('workspace.sidebar.expand')}
          aria-label={t('workspace.sidebar.openSidebar')}
        >
          <ChevronRight className="h-4 w-4 flex-shrink-0" />
        </button>
      </div>
    </>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-10 bg-neutral-100 dark:bg-neutral-800 rounded-lg" />
      ))}
    </div>
  );
}
